import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from './store/userSlice';
import { persistor } from './store';
import useIdleTimer from './hooks/useIdleTimer';
import Login from './components/Login';
import Sidebar from './components/Sidebar';
import Spinner from './components/Spinner';
import SessionExpiryModal from './components/SessionExpiryModal';
import Dashboard from './components/Dashboard';
import Products from './components/Products';
import ProductTypes from './components/ProductTypes';
import CalendarSettings from './components/CalendarSettings';
import ScheduleCalendar from './components/ScheduleCalendar';
import Coupon from './components/Coupon';
import Users from './components/Users';
import Stores from './components/Stores';
import Devices from './components/Devices';
import UserStoreRights from './components/UserStoreRights';
import Transactions from './components/Transactions';
import BonusPrizes from './components/BonusPrizes';

function App() {
  const dispatch = useDispatch();
  const { isAuthenticated, user, roleId, roleName } = useSelector(s => s.user);
  const [page, setPage] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const loadTimer = useRef(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem('lottery.sidebarCollapsed') === 'true'
  );
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sessionWarning, setSessionWarning] = useState(null);

  /* ── Each page now loads its own data on mount — no bulk load here ── */

  const handleNavigate = useCallback((p) => {
    if (p === page) return;
    setLoading(true);
    if (loadTimer.current) clearTimeout(loadTimer.current);
    loadTimer.current = setTimeout(() => { setPage(p); setLoading(false); }, 150);
    if (window.innerWidth <= 640) setSidebarCollapsed(true);
    setSidebarOpen(false);
  }, [page]);

  useEffect(() => () => { if (loadTimer.current) clearTimeout(loadTimer.current); }, []);

  const handleWarning = useCallback((sec) => setSessionWarning(sec), []);

  // Logout then hard-reload so the next session loads the freshest index.html/bundles.
  const logoutAndRefresh = useCallback(() => {
    setSessionWarning(null);
    dispatch(logout());
    persistor.flush().finally(() => window.location.reload());
  }, [dispatch]);

  const { resetTimer } = useIdleTimer(handleWarning, logoutAndRefresh, isAuthenticated);

  const handleStay = () => { resetTimer(); setSessionWarning(null); };

  const toggleSidebar = () => {
    setSidebarCollapsed(prev => {
      localStorage.setItem('lottery.sidebarCollapsed', String(!prev));
      return !prev;
    });
  };

  if (!isAuthenticated) return <Login />;

  const initials = user?.userName?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || '??';

  const renderPage = () => {
    switch (page) {
      case 'dashboard': return <Dashboard />;
      case 'products': return <Products />;
      case 'product-types': return <ProductTypes />;
      case 'calendar': return <CalendarSettings />;
      case 'schedule-calendar': return <ScheduleCalendar />;
      case 'bonus-prizes': return <BonusPrizes />;
      case 'coupon': return <Coupon />;
      case 'transactions': return <Transactions />;
      case 'users': return <Users />;
      case 'stores': return <Stores />;
      case 'devices': return <Devices />;
      case 'rights': return <UserStoreRights />;
      default: return <Dashboard />;
    }
  };

  return (
    <div className="app-layout">
      {!sidebarCollapsed && <div className="sidebar-overlay" onClick={() => setSidebarCollapsed(true)} />}
      {sidebarOpen && <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} />}
      <Sidebar
        activePage={page}
        onNavigate={handleNavigate}
        collapsed={sidebarCollapsed}
        onToggleCollapse={toggleSidebar}
        open={sidebarOpen}
        roleId={roleId}
      />
      <div className="main">
        <div className="topbar">
          <button
            className="topbar-hamburger"
            onClick={() => setSidebarOpen(o => !o)}
            aria-label={sidebarOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={sidebarOpen}>
            ☰
          </button>
          <div className="topbar-brand">
            <span className="topbar-title">Good Find - Instant Rewards</span>
          </div>
          <div className="topbar-right">
            <div className="topbar-user">
              <div className="topbar-avatar">{initials}</div>
              <span className="topbar-username">{user?.userName}</span>
            </div>
            <span className="topbar-role">{roleName}</span>
            <button className="topbar-logout" onClick={logoutAndRefresh}>Logout</button>
          </div>
        </div>
        <div className="page-content">
          {loading ? (
            <div className="page-loading"><Spinner /></div>
          ) : renderPage()}
          <div className="page-footer">
            <span>Good Find - Instant Rewards</span>
          </div>
        </div>
      </div>
      {sessionWarning && (
        <SessionExpiryModal secondsLeft={sessionWarning} onStay={handleStay} onLogout={logoutAndRefresh} />
      )}
    </div>
  );
}

export default App;
